import NotificationService, {
  NotificationPriority,
  NotificationType,
} from '../notificationService';

import {
  BusinessNotificationPriority,
  BusinessNotificationRemotePushParams,
  sendBusinessNotificationRemotePush,
} from './adapters';

class BusinessNotificationHelper {
  private static instance: BusinessNotificationHelper;

  private notificationService: NotificationService;

  private constructor() {
    this.notificationService = NotificationService.getInstance();
  }

  static getInstance(): BusinessNotificationHelper {
    if (!BusinessNotificationHelper.instance) {
      BusinessNotificationHelper.instance = new BusinessNotificationHelper();
    }
    return BusinessNotificationHelper.instance;
  }

  private async sendToUser(params: BusinessNotificationRemotePushParams): Promise<void> {
    await sendBusinessNotificationRemotePush(params);
  }

  async sendFriendInvitation(params: {
    fromUser: string;
    fromUserId: string;
    targetUserId: string;
    invitationId: string;
  }): Promise<void> {
    try {
      await this.sendToUser({
        targetUserId: params.targetUserId,
        title: '好友邀请',
        body: `${params.fromUser} 邀请你成为好友`,
        data: {
          type: 'friend_invitation',
          fromUserId: params.fromUserId,
          invitationId: params.invitationId,
          action: 'friend_invitation',
        },
        priority: 'normal',
      });
    } catch (error) {
      console.error('发送好友邀请通知失败，但好友请求已成功发送:', error);
    }
  }

  async sendFriendInvitationConfirmation(params: {
    targetUserName: string;
    invitationId: string;
  }): Promise<void> {
    await this.notificationService.sendLocalNotification({
      type: NotificationType.SYSTEM_MESSAGE,
      title: '好友请求已发送',
      body: `已向 ${params.targetUserName} 发送好友请求，等待对方回应`,
      priority: NotificationPriority.NORMAL,
      data: {
        action: 'friend_request_sent',
        invitationId: params.invitationId,
      },
      moduleType: 'friends',
    });
  }

  async sendTravelInvitation(params: {
    fromUser: string;
    groupName: string;
    groupId: string;
    targetUserId: string;
    destination?: string;
  }): Promise<void> {
    const destination = params.destination ? `前往${params.destination}` : '';
    await this.sendToUser({
      targetUserId: params.targetUserId,
      title: '拼游邀请',
      body: `${params.fromUser} 邀请你加入拼游团「${params.groupName}」${destination}`,
      data: {
        groupId: params.groupId,
        groupName: params.groupName,
        destination: params.destination,
        action: 'travel_invitation',
      },
      priority: 'high',
    });
  }

  async sendTravelInvitationConfirmation(params: {
    targetUserName: string;
    groupName: string;
    groupId: string;
  }): Promise<void> {
    await this.notificationService.sendLocalNotification({
      type: NotificationType.SYSTEM_MESSAGE,
      title: '拼游邀请已发送',
      body: `已向 ${params.targetUserName} 发送拼游邀请「${params.groupName}」`,
      priority: NotificationPriority.NORMAL,
      data: {
        action: 'travel_invitation_sent',
        groupId: params.groupId,
      },
      moduleType: 'travel',
    });
  }

  async sendTravelJoinRequest(params: {
    fromUser: string;
    groupName: string;
    groupId: string;
    requestId: string;
    targetUserId: string;
  }): Promise<void> {
    await this.sendToUser({
      targetUserId: params.targetUserId,
      title: '拼游申请',
      body: `${params.fromUser} 申请加入你的拼游团「${params.groupName}」`,
      data: {
        groupId: params.groupId,
        requestId: params.requestId,
        action: 'travel_join_request',
      },
      priority: 'high',
    });
  }

  async sendDiningInvitation(params: {
    fromUser: string;
    eventTitle: string;
    eventId: string;
    eventType: 'delivery' | 'social';
    targetUserId: string;
    restaurantName?: string;
    location?: string;
  }): Promise<void> {
    const typeText = params.eventType === 'delivery' ? '外卖拼单' : '聚餐';
    const locationText = params.restaurantName || params.location || '';

    await this.sendToUser({
      targetUserId: params.targetUserId,
      title: `${typeText}邀请`,
      body: `${params.fromUser} 邀请您参加${typeText}：${locationText}`,
      data: {
        eventId: params.eventId,
        eventType: params.eventType,
        eventTitle: params.eventTitle,
        restaurantName: params.restaurantName,
        location: params.location,
        action: 'dining_invitation',
        navigation_target: 'DiningDetail',
        navigation_params: { eventId: params.eventId },
      },
      priority: 'normal',
    });
  }

  async sendDiningInvitationConfirmation(params: {
    targetUserNames: string[];
    eventTitle: string;
    eventId: string;
    eventType: 'delivery' | 'social';
  }): Promise<void> {
    const typeText = params.eventType === 'delivery' ? '外卖拼单' : '聚餐';
    const namesText = params.targetUserNames.join('、');

    await this.notificationService.sendLocalNotification({
      type: NotificationType.SYSTEM_MESSAGE,
      title: `${typeText}邀请已发送`,
      body: `已向 ${namesText} 发送${typeText}邀请`,
      priority: NotificationPriority.NORMAL,
      data: {
        action: 'dining_invitation_sent',
        eventId: params.eventId,
        eventType: params.eventType,
        invited_count: params.targetUserNames.length,
      },
      moduleType: 'dining',
    });
  }

  async sendSystemNotification(params: {
    title: string;
    body: string;
    priority?: BusinessNotificationPriority;
    data?: Record<string, any>;
  }): Promise<void> {
    await this.notificationService.sendLocalNotification({
      type: NotificationType.SYSTEM_MESSAGE,
      title: params.title,
      body: params.body,
      priority:
        params.priority === 'high' ? NotificationPriority.HIGH : NotificationPriority.NORMAL,
      data: params.data || {},
      moduleType: 'system',
    });
  }

  async sendGroupOrderInvitation(params: {
    fromUser: string;
    fromUserId: string;
    targetUserId: string;
    orderTitle: string;
    orderId: string;
  }): Promise<void> {
    try {
      await this.sendToUser({
        targetUserId: params.targetUserId,
        title: '🛒 团购邀请',
        body: `${params.fromUser}邀请您一起团购"${params.orderTitle}"`,
        data: {
          type: 'grouporder_invitation',
          orderId: params.orderId,
          orderTitle: params.orderTitle,
          fromUserId: params.fromUserId,
          action: 'grouporder_invitation',
        },
        priority: 'normal',
      });
    } catch (error) {
      console.error('❌ 发送团购邀请通知失败:', error);
      throw error;
    }
  }
}

export default BusinessNotificationHelper;
